import { AccountModel, LocationModel, UserModel, WorklistViewAccessListModel, WorklistViewModel } from 'models';
import { WorklistViewAccessTypeModel } from 'models/WorklistViewAccessTypeModel';

import { apiClient } from 'core/api/globals';
import { findOrThrow } from 'core/utils';

import { EditFormValues } from '../types';

function copyModelToForm(
  model: WorklistViewAccessListModel | null,
  allAccessModes: WorklistViewAccessTypeModel[],
  allUsers: UserModel[],
  allWorklists: WorklistViewModel[],
  allLocations: LocationModel[],
  allAccounts: AccountModel[],
): EditFormValues {
  if (model == null) {
    return {
      users: [],
      access: null,
      worklist: null,
      active: true,
      default: false,
      locations: [],
      accounts: [],
    };
  }

  const access = findOrThrow(
    allAccessModes,
    (a) => a.id === model.worklistViewAccessTypeId,
    `Could not find WorklistViewAccess with id: ${model.worklistViewAccessTypeId}.`,
  );

  const users = model.users ? allUsers.filter((u) => model.users.map((s) => s.id).includes(u.id)) : [];

  const worklist = findOrThrow(allWorklists, (w) => w.id === model.worklistViewId, `Could not find worklist with id: ${model.worklistViewId}.`);

  const locations = model.locations ? allLocations.filter((item) => model.locations.map((m) => m.id).includes(item.id)) : [];

  const accounts = model.accounts ? allAccounts.filter((item) => model.accounts.map((m) => m.id).includes(item.id)) : [];

  return {
    users,
    access,
    worklist,
    active: model.active,
    default: model.default,
    locations,
    accounts,
  };
}

function copyFormToModel(original: WorklistViewAccessListModel | null, form: EditFormValues): WorklistViewAccessListModel {
  if (form.users == null) {
    throw new Error('Cannot save because user is null or undefined.');
  }

  if (form.worklist == null) {
    throw new Error('Cannot save because worklist is null or undefined.');
  }
  if (form.access == null) {
    throw new Error('Cannot save because access is null or undefined.');
  }

  return {
    id: original == null ? 0 : original.id,
    worklistViewId: form.worklist.id,
    worklistViewAccessTypeId: form.access.id,
    users: form.users,
    active: form.active,
    default: form.default,
    locations: form.locations,
    accounts: form.accounts,
  };
}

/**
 * Load specific users by their IDs
 * Uses a fallback approach compatible with existing backend
 */
async function loadUsersByIds(userIds: number[]): Promise<UserModel[]> {
  if (userIds.length === 0) return [];

  try {
    // Try to use ID filtering if backend supports it
    const { data: users } = await apiClient.users.getAllForKendoGrid({
      filter: {
        logic: 'and',
        filters: [
          {
            field: 'id',
            operator: 'in',
            value: userIds,
          },
          {
            field: 'email',
            operator: 'isnotempty',
          },
        ],
      },
    });

    return users;
  } catch (error) {
    console.warn(
      'Backend does not support ID filtering, falling back to load all users and filter client-side',
      error instanceof Error ? error.message : 'Unknown error',
    );

    // Fallback: Load all users with email filter only, then filter client-side
    const { data: allUsers } = await apiClient.users.getAllForKendoGrid({
      filter: {
        logic: 'and',
        filters: [
          {
            field: 'email',
            operator: 'isnotempty',
          },
        ],
      },
    });

    // Filter to only the requested user IDs
    return allUsers.filter((user) => userIds.includes(user.id));
  }
}

/**
 * Temporary fallback: Use original approach until API compatibility is resolved
 * This ensures the page loads properly without errors
 */
async function initialize(id: number | null | undefined) {
  const modelPromise =
    id == null
      ? new Promise<null>((resolve) => {
          resolve(null);
        })
      : apiClient.workListViewClient.getWorkListViewAccess(id);

  const [model, allWorklists, { data: allUsers }, allAccessModes, allLocations, allWorklistAccessType, allAccounts] = await Promise.all([
    modelPromise,
    apiClient.workListViewClient.getViews(true, undefined, 'Exam'),
    apiClient.users.getAllForKendoGrid({
      filter: {
        logic: 'and',
        filters: [
          {
            field: 'email',
            operator: 'isnotempty',
          },
        ],
      },
    }), // Temporary: back to original approach to ensure compatibility
    apiClient.workListViewClient.getAllWorklistViewAccess(),
    apiClient.locationClient.getAllLocations(),
    apiClient.workListViewClient.getAllWorklistViewAccessType(),
    apiClient.accountClient.getAllAccounts(),
  ]);

  return {
    model,
    allWorklists,
    allUsers,
    allAccessModes,
    allLocations,
    allWorklistAccessType,
    allAccounts,
    form: copyModelToForm(model, allWorklistAccessType, allUsers, allWorklists, allLocations, allAccounts),
  };
}

/**
 * Enhanced initialization with progress tracking for better UX
 * @param id - The access rule ID for edit mode, null for new mode
 * @param onProgress - Callback to track loading progress
 */
async function initializeWithProgress(id: number | null | undefined, onProgress?: (step: string) => void) {
  try {
    onProgress?.('Loading worklist configuration...');

    // Phase 1: Essential data
    const [model, allWorklists, allWorklistAccessType] = await Promise.all([
      id == null ? Promise.resolve(null) : apiClient.workListViewClient.getWorkListViewAccess(id),
      apiClient.workListViewClient.getViews(true, undefined, 'Exam'),
      apiClient.workListViewClient.getAllWorklistViewAccessType(),
    ]);

    onProgress?.('Loading assigned users...');

    // Phase 2: Only assigned users
    let assignedUsers: UserModel[] = [];
    if (model?.users && model.users.length > 0) {
      const userIds = model.users.map((u) => u.id);
      assignedUsers = await loadUsersByIds(userIds);
    }

    onProgress?.('Loading additional configuration...');

    // Phase 3: Secondary data
    const [allAccessModes, allLocations, allAccounts] = await Promise.all([
      apiClient.workListViewClient.getAllWorklistViewAccess(),
      apiClient.locationClient.getAllLocations(),
      apiClient.accountClient.getAllAccounts(),
    ]);

    onProgress?.('Finalizing setup...');

    const result = {
      model,
      allWorklists,
      allUsers: assignedUsers,
      allAccessModes,
      allLocations,
      allWorklistAccessType,
      allAccounts,
      form: copyModelToForm(model, allWorklistAccessType, assignedUsers, allWorklists, allLocations, allAccounts),
    };

    onProgress?.('Ready!');
    return result;
  } catch (error) {
    onProgress?.('Error loading data');
    throw error instanceof Error ? error : new Error('Unknown error occurred during initialization');
  }
}

/**
 * Search users with server-side filtering and pagination
 * Replaces the need to load ALL users upfront
 */
async function searchUsers(query: string, limit = 50): Promise<UserModel[]> {
  if (!query || query.length < 2) {
    return [];
  }

  try {
    const { data: users } = await apiClient.users.getAllForKendoGrid({
      take: limit,
      filter: {
        logic: 'and',
        filters: [
          {
            logic: 'or',
            filters: [
              {
                field: 'email',
                operator: 'contains',
                value: query,
              },
              {
                field: 'firstName',
                operator: 'contains',
                value: query,
              },
              {
                field: 'lastName',
                operator: 'contains',
                value: query,
              },
            ],
          },
          {
            field: 'email',
            operator: 'isnotempty',
          },
        ],
      },
    });

    return users;
  } catch (error) {
    console.error('Error searching users:', error instanceof Error ? error.message : 'Unknown error');
    return [];
  }
}

async function save(form: EditFormValues, original: WorklistViewAccessListModel | null): Promise<WorklistViewAccessListModel> {
  const newModel = copyFormToModel(original, form);
  if (newModel.id === 0) {
    const id = await apiClient.workListViewClient.createUpdateWorkListViewAccess(newModel);
    newModel.id = id;
  } else {
    await apiClient.workListViewClient.createUpdateWorkListViewAccess(newModel);
  }

  return newModel;
}

async function deleteAccess(id: number) {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-call
  await apiClient.workListViewClient.deleteWorkListViewAccess(id);
}

export const WorklistViewUserEditService = {
  initialize,
  initializeWithProgress,
  searchUsers,
  loadUsersByIds,
  copyModelToForm,
  copyFormToModel,
  save,
  deleteAccess,
};
